import {useRef, useState, type ChangeEvent} from 'react';
import { orderBy } from "@/lib/collections";
import type { FieldErrors, UseFormSetValue } from 'react-hook-form';
import {getCity, getReparateurByCodePostal, removeJoursFeries} from "@/lib/helpers";
import useBookingUrl from "@/hooks/use-booking-url";
import Text from "@/components/forms/text";
import Select from "@/components/forms/select";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSelectedContract } from '@/hooks/api/use-contracts';
import type { AjoutReparateurValues } from '@/hooks/api/use-accidents';
import { isWakamContract } from '@/lib/contracts';
import { formatDate } from '@/lib/dates';
import type { Accident } from '@/types/api/accident';

export interface Reparateur {
    id: string;
    raisonSociale?: string;
    rue1?: string;
    datesVisite: string[];
    [key: string]: unknown;
}

interface FormAjoutReparateurProps {
    accident: Accident;
    handleChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    errors: FieldErrors<AjoutReparateurValues>;
    values: AjoutReparateurValues;
    setValue: UseFormSetValue<AjoutReparateurValues>;
}

const FormAjoutReparateur = (props: FormAjoutReparateurProps) => {
    const {accident, handleChange, errors, values, setValue} = props
    const garanties = accident.garanties as Record<string, unknown>[] | undefined
    const categorieCode = accident.categorieCode
    const isWakam = isWakamContract(useSelectedContract())
    const [city, setCity] = useState<unknown[]>([])
    const [reparateurs, setReparateurs] = useState<Reparateur[]>([])
    const [missions, setMissions] = useState<string[]>([])
    const [addressReparateur, setAddressReparateurs] = useState<string | null | undefined>(null)
    /* Code postal saisi sans aucun garage référencé : la v1 plantait sur la liste vide */
    const [aucunGarage, setAucunGarage] = useState(false)
    /* Dernier code postal recherché : une réponse arrivée après une nouvelle saisie est ignorée */
    const codePostalRecherche = useRef<string | null>(null)
    const bookingUrl = useBookingUrl('rdv_garage')

    /* Recherche du reparateur par code postal */
    const setPostalCode = (postalCode: string) => {
        codePostalRecherche.current = postalCode
        fetchCityByPostalCode(postalCode)
        fetchReparateurs(postalCode)
    }

    /* ajout l'id du reparateur dans le form */
    const changeValueReparateurVisites = (reparateur: string, missionsReparateur: string[]) => {
        setValue('reparateur', reparateur)
        if (!(!garanties?.find((g) => g.code_famille === 'DTA') && ['104.2', '104.3', '104.5', '104.1'].includes(categorieCode ?? ''))){
            if(missionsReparateur[0]){
                setValue('visit', formatDate(missionsReparateur[0]))
            } else {
                setValue('visit', '')
            }
        }
    }

    /* Function async qui va chercher les villes via le code postal */
    async function fetchCityByPostalCode(value: string){
        const cities = await getCity<unknown[]>(value)
        if (codePostalRecherche.current !== value) return
        setCity(cities)
    }

    /* Function async qui va chercher les reparateur via le code postal */
    async function fetchReparateurs(postalCode: string){
        const arrayReparateurs = await getReparateurByCodePostal<Reparateur[]>(postalCode)
        if (codePostalRecherche.current !== postalCode) return
        const reparateurs = orderBy(arrayReparateurs, ['raisonSociale'], ['asc'])

        setAucunGarage(reparateurs.length === 0)
        if (reparateurs.length === 0) {
            reinitialiserReparateur()
            return
        }

        reparateurs[0].datesVisite= removeJoursFeries(reparateurs[0].datesVisite)
        setReparateurs(reparateurs)
        setMissions(reparateurs[0].datesVisite)
        setAddressReparateurs(reparateurs[0].rue1)

        changeValueReparateurVisites(reparateurs[0].id, reparateurs[0].datesVisite)
    }

    /* Vide le choix précédent : nouveau code postal en cours de saisie ou sans garage */
    const reinitialiserReparateur = () => {
        setReparateurs([])
        setMissions([])
        setAddressReparateurs(null)
        setValue('reparateur', '')
        setValue('visit', '')
    }

    /* Ajout l'addresse du réparateur et les missions disponible apres la selection du reparateur */
    const getAdresseReparateur = (id: string) => {
        const item = reparateurs.find((r) => r.id === id) as Reparateur

        item.datesVisite = removeJoursFeries(item.datesVisite)

        setAddressReparateurs(item.rue1)
        setMissions(item.datesVisite)

        changeValueReparateurVisites(item.id, item.datesVisite)
    }

    return (
        <div className="space-y-6">
            <div>
                <Text
                    name="cpreparateur"
                    required
                    onChange={(e) => {
                        handleChange(e)
                        if (e.target.value.length === 5) {
                            setPostalCode(e.target.value)
                        } else {
                            codePostalRecherche.current = null
                            setCity([])
                            setAucunGarage(false)
                            reinitialiserReparateur()
                        }
                    }}
                    label={'Code postal du garage / réparateur'}
                    value={values.cpreparateur}
                    error={errors.cpreparateur}
                    maxLength={5}
                />
            </div>

            <div>
                {/* Sans garage pour ce code postal, un message plutôt qu'une liste vide */}
                {aucunGarage ? (
                    <Alert variant="warning">
                        <AlertDescription>Aucun garage trouvé pour ce code postal.</AlertDescription>
                    </Alert>
                ) : (
                    <Select
                        required
                        name={"reparateur"}
                        error={errors.reparateur}
                        label={'Garage / Réparateur'}
                        value={values.reparateur}
                        disabled={city.length === 0 || reparateurs.length === 0}
                        onChange={(e) => getAdresseReparateur(e.target.value)}
                    >
                        {
                            reparateurs.map((r, i) => (
                                <option key={i} value={r.id}>{r.raisonSociale}</option>
                            ))
                        }
                    </Select>
                )}
                {bookingUrl && <p className={'mt-2 text-sm italic'}>Vous ne trouvez pas votre garage ? <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className={'underline italic'}>Prenons rendez-vous</a></p>}
            </div>

            {
                values.reparateur && (
                    <Alert variant="warning">
                        <AlertDescription>Adresse : {addressReparateur}</AlertDescription>
                    </Alert>
                )
            }

            {
               !garanties?.find((g) => g.code_famille === 'DTA') && ['104.2', '104.3', '104.5', '104.1'].includes(categorieCode ?? '') ? null : isWakam || aucunGarage ? null : <div className="space-y-2">
                    <Select
                        name={"visit"}
                        error={errors.visit}
                        label={'Date de passage de l\'expert'}
                        value={values.visit}
                        disabled={city.length === 0 || missions.length === 0}
                    >
                        {
                            missions.map((m, i) => (
                                <option key={i} value={formatDate(m)}>{formatDate(m)}</option>
                            ))
                        }
                    </Select>
                    <p className={'text-destructive text-sm font-semibold'}>Cette date est susceptible d'être modifiée, un mail de confirmation vous sera envoyé lorsque l'expertise sera validée.</p>
                </div>
            }
        </div>
    );
};

export default FormAjoutReparateur;